import { injectStyles } from "../../utils/core/injectStyles.js";
import { parseElementConfig, mergeAttributeMaps } from "../../utils/core/attributeParser.js";
import { AnimationStateManager, PerformanceOptimizer } from "../../utils/animation/animationStateManager.js";

const COMPONENT_CSS_ID = 'wb-3d-card-hover-styles';
const componentCSS = `
  /* FlowBitz - 3D Card Hover Effect Styles */
  .wb-hover3d__inner {
    transform-style: preserve-3d;
    will-change: transform;
    transition: transform 300ms linear;
    backface-visibility: hidden;
    transform-origin: center center;
    width: 100%;
    height: 100%;
    /* Inherit visual styles from parent via CSS cascade */
    box-sizing: border-box;
  }
`;

function ensureStylesInjected() {
  injectStyles(COMPONENT_CSS_ID, componentCSS);
}

class Hover3DCard {
  constructor(root, config, instancesMap) {
    this.root = root;
    this.config = config;
    this.instancesMap = instancesMap;
    this.wrapper = null;
    this.boundMouseMove = null;
    this.boundMouseEnter = null;
    this.boundMouseLeave = null;
    this.componentName = '3d-card-hover';
    this.movedRootClasses = [];
    this.movedRootId = null;
    this.originalTransform = null; // Store original transform to preserve it
    this.init();
  }

  init() {
    const tag = (this.root.tagName || '').toUpperCase();
    if (tag !== 'DIV' && tag !== 'BUTTON') {
      console.warn('3d-card-hover: Root element must be a <div> or <button>', this.root);
      return;
    }

    if (this.root.dataset.hover3dInitialized === 'true') return;

    ensureStylesInjected();

    // Save all original root classes (BUT DON'T REMOVE - keep them on root)
    // Classes need to stay on root for CSS selectors (e.g., .product-grid > .product-card)
    // and also on wrapper for visual styling
    this.movedRootClasses = Array.from(this.root.classList);
    // DON'T remove classes from root - keep them for CSS selectors and layout

    // 2) Create inner wrapper
    const wrapper = document.createElement('div');
    wrapper.classList.add('wb-hover3d__inner');

    // Copy classes to wrapper so it inherits visual styling from root
    // Classes stay on root (for CSS selectors) AND are added to wrapper (for visual appearance)
    if (this.movedRootClasses.length) {
      wrapper.classList.add(...this.movedRootClasses);
    }

    // Keep ID on root - don't move it to wrapper
    // ID should stay on the element with wb-component attribute

    // Keep ALL original styles on root (don't separate or move them)
    // Root element should maintain all its original attributes
    const originalStyle = this.root.getAttribute('style') || '';
    
    // Extract original transform if it exists (for merging with 3D transforms)
    const transformMatch = originalStyle.match(/transform\s*:\s*([^;]+)/i);
    if (transformMatch) {
      this.originalTransform = transformMatch[1].trim();
    }
    
    // Add perspective to root styles (keep all other styles intact)
    let rootStyle = originalStyle;
    if (rootStyle && !rootStyle.includes('perspective:')) {
      // Add perspective to existing styles
      rootStyle = `${rootStyle}; perspective: ${this.config.perspective}px`;
    } else if (!rootStyle) {
      // Only perspective if no other styles
      rootStyle = `perspective: ${this.config.perspective}px`;
    } else {
      // Replace existing perspective
      rootStyle = rootStyle.replace(/perspective\s*:\s*[^;]+/gi, `perspective: ${this.config.perspective}px`);
    }
    
    // Keep all styles on root
    this.root.setAttribute('style', rootStyle);
    
    // Wrapper is minimal - just for 3D transform effect
    // It will inherit visual styles from root via CSS classes
    // Only add essential 3D transform properties
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';

    // Default transition if not already set
    if (!wrapper.style.transition) {
      wrapper.style.transition = this.config.transition;
    }

    // Performance optimization
    PerformanceOptimizer.optimizeForAnimation(wrapper, {
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      perspective: ''
    });

    // 3) Move children from root → wrapper
    while (this.root.firstChild) {
      wrapper.appendChild(this.root.firstChild);
    }

    // 4) Insert wrapper into root
    this.root.appendChild(wrapper);
    this.wrapper = wrapper;

    // 5) Attach events to root (the element with wb-component)
    // Events should be on the original element, not the wrapper
    const throttledMove = PerformanceOptimizer.throttle((e) => this.handleMouseMove(e), 16);
    this.boundMouseMove = (e) => throttledMove.call(this, e);
    this.boundMouseEnter = () => this.handleMouseEnter();
    this.boundMouseLeave = () => this.handleMouseLeave();

    this.root.addEventListener('mousemove', this.boundMouseMove);
    this.root.addEventListener('mouseenter', this.boundMouseEnter);
    this.root.addEventListener('mouseleave', this.boundMouseLeave);

    this.root.dataset.hover3dInitialized = 'true';
  }

  /**
   * Merge original transform with 3D hover transform
   * Preserves original transform (e.g., translate(0px, 0px)) and adds 3D rotation
   */
  mergeTransform(rotateX, rotateY) {
    const hover3D = `translateZ(0) rotateY(${rotateY}deg) rotateX(${rotateX}deg)`;
    
    if (this.originalTransform) {
      // If original transform exists, combine them
      // Original transform first, then 3D transforms
      return `${this.originalTransform} ${hover3D}`;
    }
    
    return hover3D;
  }

  handleMouseEnter() {
    // Set state on wrapper (root is just a container)
    AnimationStateManager.setAnimatingState(this.wrapper, 'wb-hover3d');
    AnimationStateManager.dispatchLifecycleEvent(this.root, 'start', this.componentName);
  }

  handleMouseMove(e) {
    if (!this.wrapper) return;
    // Use root element's bounding rect since events are on root
    const rect = this.root.getBoundingClientRect();
    const dx = (e.clientX - rect.left - rect.width / 2);
    const dy = (e.clientY - rect.top - rect.height / 2);
    const x = dx / this.config.rotateDivisor;
    const y = -dy / this.config.rotateDivisor;
    
    // Apply 3D transform to wrapper (which contains the content)
    this.wrapper.style.transform = this.mergeTransform(x, y);
  }

  handleMouseLeave() {
    if (!this.wrapper) return;
    
    // Merge original transform with reset 3D rotation
    this.wrapper.style.transform = this.mergeTransform(0, 0);
    
    AnimationStateManager.setCompletedState(this.wrapper, 'wb-hover3d');
    AnimationStateManager.dispatchLifecycleEvent(this.root, 'complete', this.componentName);
  }

  destroy() {
    // Remove event listeners from root
    if (this.boundMouseMove) this.root?.removeEventListener('mousemove', this.boundMouseMove);
    if (this.boundMouseEnter) this.root?.removeEventListener('mouseenter', this.boundMouseEnter);
    if (this.boundMouseLeave) this.root?.removeEventListener('mouseleave', this.boundMouseLeave);

    // Move children back to root
    if (this.wrapper) {
      PerformanceOptimizer.cleanupAfterAnimation(this.wrapper, { restoreOriginal: true });

      while (this.wrapper.firstChild) {
        this.root.appendChild(this.wrapper.firstChild);
      }

      // Classes and ID are already on root (never removed), so no need to restore
      // Just clean up wrapper - remove classes and styles
      if (this.movedRootClasses.length) {
        this.movedRootClasses.forEach(cls => {
          this.wrapper.classList.remove(cls);
        });
      }
      this.wrapper.style.width = '';
      this.wrapper.style.height = '';
      this.wrapper.style.transform = '';

      this.root.removeChild(this.wrapper);
      this.wrapper = null;
    }

    delete this.root.dataset.hover3dInitialized;
  }
};

class CardHover3DAnimator {
  constructor() {
    this.componentName = '3d-card-hover';
    this.instances = new Map();
    this.defaultConfig = {
      perspective: 800,
      rotateDivisor: 15,
      transition: 'transform 300ms linear'
    };
    this.attributeMap = mergeAttributeMaps({
      perspective: { attribute: 'wb-perspective', type: 'number', parser: parseInt, min: 100, max: 5000 },
      rotateDivisor: { attribute: 'wb-rotate-divisor', type: 'number', parser: parseFloat, min: 1, max: 100 },
      transition: { attribute: 'wb-transition', type: 'string' }
    });
  };

  parseConfig(element) {
    return parseElementConfig(element, this.defaultConfig, this.attributeMap);
  };

  initElement(element) {
    if (this.instances.has(element)) return this.instances.get(element);
    const config = this.parseConfig(element);
    const instance = new Hover3DCard(element, config, this.instances);
    this.instances.set(element, instance);
    return instance;
  };

  initAll() {
    const elements = document.querySelectorAll('[wb-component="3d-card-hover"]');
    elements.forEach((el) => this.initElement(el));
  };

  refresh() { return this; };

  destroyAll() {
    this.instances.forEach((instance) => instance.destroy && instance.destroy());
    this.instances.clear();
  };
};

const cardHover3DAnimator = new CardHover3DAnimator();

document.addEventListener('DOMContentLoaded', () => {
  cardHover3DAnimator.initAll();
});

export default cardHover3DAnimator;
export { Hover3DCard, CardHover3DAnimator };